/**
 * SmsConsentAck - reusable, metadata-driven SMS opt-in checkbox + disclosure.
 *
 * Rendered only when the claim metadata carried an `sms_consent` block (the
 * backend gates that on the tenant's `sms` capability), so the client never
 * inspects a feature flag. The disclosure copy comes from the API, never
 * hardcoded. Opt-in is voluntary: an unticked box means "do not text me", so it
 * never blocks submission (unlike a required DisclaimerAck).
 *
 * Surface-agnostic: drop it on any claim submit step (consumer, sage, public).
 */

import { MessageSquare } from 'lucide-react'

import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import { cn } from '@/shared/lib/utils'

export interface SmsConsentAckProps {
  /** Tenant-authored disclosure copy from the claim metadata. */
  disclosure: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  className?: string
}

export function SmsConsentAck({
  disclosure,
  checked,
  onCheckedChange,
  className,
}: SmsConsentAckProps) {
  return (
    <div className={className}>
      <Label
        className={cn(
          'flex cursor-pointer items-start gap-2.5 rounded-[8px] border-[0.5px]',
          'border-[var(--rule-strong)] bg-[var(--bg-subtle)] p-4 font-normal',
        )}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onCheckedChange(v === true)}
          className="mt-0.5"
        />
        <span className="flex items-start gap-1.5 text-[12px] leading-[1.6] text-[var(--ink-2)]">
          <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
          <span>{disclosure}</span>
        </span>
      </Label>
    </div>
  )
}
